/**
 * useFrameCapture Hook
 * Extracts frames from video stream for live analysis
 */
import { useCallback, useRef } from "react";

interface UseFrameCaptureReturn {
  extractFrame: (videoElement: HTMLVideoElement) => Promise<Blob | null>;
  startRecording: (
    videoElement: HTMLVideoElement,
    maxDuration: number
  ) => Promise<Blob | null>;
  stopRecording: () => void;
  isRecording: boolean;
}

export const useFrameCapture = (): UseFrameCaptureReturn => {
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const resolveRef = useRef<((blob: Blob | null) => void) | null>(null);
  const isRecordingRef = useRef(false);

  const extractFrame = useCallback(
    async (videoElement: HTMLVideoElement): Promise<Blob | null> => {
      if (!videoElement || videoElement.readyState < 2) {
        return null;
      }

      const canvas = document.createElement("canvas");
      canvas.width = videoElement.videoWidth;
      canvas.height = videoElement.videoHeight;

      const ctx = canvas.getContext("2d");
      if (!ctx) return null;

      ctx.drawImage(videoElement, 0, 0);

      return new Promise((resolve) => {
        canvas.toBlob(
          (blob) => resolve(blob),
          "image/jpeg",
          0.8 // 80% quality for faster upload
        );
      });
    },
    []
  );

  const stopRecording = useCallback(() => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === "recording"
    ) {
      mediaRecorderRef.current.stop();
      isRecordingRef.current = false;
    }
  }, []);

  const startRecording = useCallback(
    async (
      videoElement: HTMLVideoElement,
      maxDuration: number
    ): Promise<Blob | null> => {
      return new Promise((resolve, reject) => {
        const stream = videoElement.srcObject as MediaStream;
        if (!stream) {
          reject(new Error("No video stream available"));
          return;
        }

        // Reset chunks for new recording (Prevents concatenation)
        chunksRef.current = [];
        resolveRef.current = resolve;

        const mediaRecorder = new MediaRecorder(stream, {
          mimeType: "video/webm",
        });

        mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            chunksRef.current.push(event.data);
          }
        };

        mediaRecorder.onstop = () => {
          const blob = new Blob(chunksRef.current, { type: "video/webm" });
          isRecordingRef.current = false;
          if (resolveRef.current) {
            resolveRef.current(blob);
            resolveRef.current = null;
          }
          mediaRecorderRef.current = null;
        };

        mediaRecorder.onerror = (event) => {
          isRecordingRef.current = false;
          reject(new Error("Recording failed"));
        };

        mediaRecorder.start();
        mediaRecorderRef.current = mediaRecorder;
        isRecordingRef.current = true;

        // Auto-stop after max duration
        setTimeout(() => {
          if (mediaRecorder.state === "recording") {
            mediaRecorder.stop();
          }
        }, maxDuration);
      });
    },
    []
  );

  return {
    extractFrame,
    startRecording,
    stopRecording,
    isRecording: isRecordingRef.current,
  };
};
